import * as moment from 'moment';
import { Component, Inject, Input, LOCALE_ID, OnInit } from '@angular/core';
import { EMPTY, Subscription, forkJoin } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { expand, finalize, pairwise } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';

import { ChartDataset, ChartLabel } from 'src/app/core/entities/chart-dataset';
import { AutoUnsub } from 'src/app/core/decorators/auto-unsub.decorator';
import { ChartDataPoint } from 'src/app/core/entities/chart-data-point.entity';
import { ChartDescription } from 'src/app/core/entities/chart-description';
import { ChartEvaluation } from 'src/app/core/entities/chart-evaluation';
import { ChartService } from 'src/app/core/services/chart.service';
import { EvaluationService } from 'src/app/core/services/evaluation.service';
import { Feature } from 'src/app/core/entities/feature.entity';
import { FeatureService } from 'src/app/core/services/feature.service';
import { Patient } from 'src/app/core/entities/patient.entity';

@Component({
  selector: 'app-sf36',
  templateUrl: './sf36.component.html',
  styleUrls: ['./sf36.component.css'],
})
@AutoUnsub()
export class Sf36Component implements OnInit {
  @Input() public patientProfile: Patient = new Patient();
  private _observers: Subscription = new Subscription();
  private _features: any = {};
  public evaluations = {
    sf36PhysicalHealth: <ChartEvaluation>{
      evaluationCode: EvaluationService.EVALUATION_SF36,
      hidden: true,
      title: $localize`:@@patient_evaluationSF36_SF36PhysicalHealthTitle:Average Physical Score (PCS)`,
      features: [
        {
          featureId: FeatureService.FEATURE_SF36_PHYSICAL_HEALTH_SCR,
          label: $localize`:@@patient_evaluationSF36_featureSF36ScoreLabel:Score`,
          min: 0,
          max: 100,
          chartDatasetOption: {
            type: 'bar',
            color: '#8893aa',
            nbSteps: 10,
            tooltip: {
              label: $localize`:@@patient_evaluationSF36_featureSF36ScoreLabel:Score`,
              unit: '',
            },
          },
        },
      ],
      horizontalLimitLines: [
        {
          label: $localize`:@@patient_evaluationSF36_featureSF36HorizontalLimitLineYLabel:Maximum severity`,
          color: '#3d3d40',
          assignToAxisIds: {
            y: 100,
          },
        },
      ],
    },
    sf36MentalHealth: <ChartEvaluation>{
      evaluationCode: EvaluationService.EVALUATION_SF36,
      hidden: true,
      title: $localize`:@@patient_evaluationSF36_SF36MentalHealthTitle:Mean Mental Score (MCS)`,
      features: [
        {
          featureId: FeatureService.FEATURE_SF36_MENTAL_HEALTH_SCR,
          label: $localize`:@@patient_evaluationSF36_featureSF36ScoreLabel:Score`,
          min: 0,
          max: 100,
          chartDatasetOption: {
            type: 'bar',
            color: '#8893aa',
            nbSteps: 10,
            tooltip: {
              label: $localize`:@@patient_evaluationSF36_featureSF36ScoreLabel:Score`,
              unit: '',
            },
          },
        },
      ],
      horizontalLimitLines: [
        {
          label: $localize`:@@patient_evaluationSF36_featureSF36HorizontalLimitLineYLabel:Maximum severity`,
          color: '#3d3d40',
          assignToAxisIds: {
            y: 100,
          },
        },
      ],
    },
    sf361: <ChartEvaluation>{
      evaluationCode: EvaluationService.EVALUATION_SF36,
      hidden: false,
      title: $localize`:@@patient_evaluationSF36_SF361Title:Physical Activity (PF)`,
      order: 0,
      features: [
        {
          featureId: FeatureService.FEATURE_SF36_1_SCR,
          label: $localize`:@@patient_evaluationSF36_featureSF36ScoreLabel:Score`,
          min: 0,
          max: 100,
          chartDatasetOption: {
            type: 'bar',
            color: '#8893aa',
            nbSteps: 10,
            tooltip: {
              label: $localize`:@@patient_evaluationSF36_featureSF36ScoreLabel:Score`,
              unit: '',
            },
          },
        },
      ],
      horizontalLimitLines: [
        {
          label: $localize`:@@patient_evaluationSF36_featureSF36HorizontalLimitLineYLabel:Maximum severity`,
          color: '#3d3d40',
          assignToAxisIds: {
            y: 100,
          },
        },
      ],
    },
    sf362: <ChartEvaluation>{
      evaluationCode: EvaluationService.EVALUATION_SF36,
      hidden: false,
      title: $localize`:@@patient_evaluationSF36_SF362Title:Vitality (VT)`,
      order: 4,
      features: [
        {
          featureId: FeatureService.FEATURE_SF36_2_SCR,
          label: $localize`:@@patient_evaluationSF36_featureSF36ScoreLabel:Score`,
          min: 0,
          max: 100,
          chartDatasetOption: {
            type: 'bar',
            color: '#8893aa',
            nbSteps: 10,
            tooltip: {
              label: $localize`:@@patient_evaluationSF36_featureSF36ScoreLabel:Score`,
              unit: '',
            },
          },
        },
      ],
      horizontalLimitLines: [
        {
          label: $localize`:@@patient_evaluationSF36_featureSF36HorizontalLimitLineYLabel:Maximum severity`,
          color: '#3d3d40',
          assignToAxisIds: {
            y: 100,
          },
        },
      ],
    },
    sf363: <ChartEvaluation>{
      evaluationCode: EvaluationService.EVALUATION_SF36,
      hidden: false,
      title: $localize`:@@patient_evaluationSF36_SF363Title:Limitations due to Physical State (RP)`,
      order: 1,
      features: [
        {
          featureId: FeatureService.FEATURE_SF36_3_SCR,
          label: $localize`:@@patient_evaluationSF36_featureSF36ScoreLabel:Score`,
          min: 0,
          max: 100,
          chartDatasetOption: {
            type: 'bar',
            color: '#8893aa',
            nbSteps: 10,
            tooltip: {
              label: $localize`:@@patient_evaluationSF36_featureSF36ScoreLabel:Score`,
              unit: '',
            },
          },
        },
      ],
      horizontalLimitLines: [
        {
          label: $localize`:@@patient_evaluationSF36_featureSF36HorizontalLimitLineYLabel:Maximum severity`,
          color: '#3d3d40',
          assignToAxisIds: {
            y: 100,
          },
        },
      ],
    },
    sf364: <ChartEvaluation>{
      evaluationCode: EvaluationService.EVALUATION_SF36,
      hidden: false,
      title: $localize`:@@patient_evaluationSF36_SF364Title:Mental Health (MH)`,
      order: 5,
      features: [
        {
          featureId: FeatureService.FEATURE_SF36_4_SCR,
          label: $localize`:@@patient_evaluationSF36_featureSF36ScoreLabel:Score`,
          min: 0,
          max: 100,
          chartDatasetOption: {
            type: 'bar',
            color: '#8893aa',
            nbSteps: 10,
            tooltip: {
              label: $localize`:@@patient_evaluationSF36_featureSF36ScoreLabel:Score`,
              unit: '',
            },
          },
        },
      ],
      horizontalLimitLines: [
        {
          label: $localize`:@@patient_evaluationSF36_featureSF36HorizontalLimitLineYLabel:Maximum severity`,
          color: '#3d3d40',
          assignToAxisIds: {
            y: 100,
          },
        },
      ],
    },
    sf365: <ChartEvaluation>{
      evaluationCode: EvaluationService.EVALUATION_SF36,
      hidden: false,
      title: $localize`:@@patient_evaluationSF36_SF365Title:Role-emotional (RE)`,
      order: 2,
      features: [
        {
          featureId: FeatureService.FEATURE_SF36_5_SCR,
          label: $localize`:@@patient_evaluationSF36_featureSF36ScoreLabel:Score`,
          min: 0,
          max: 100,
          chartDatasetOption: {
            type: 'bar',
            color: '#8893aa',
            nbSteps: 10,
            tooltip: {
              label: $localize`:@@patient_evaluationSF36_featureSF36ScoreLabel:Score`,
              unit: '',
            },
          },
        },
      ],
      horizontalLimitLines: [
        {
          label: $localize`:@@patient_evaluationSF36_featureSF36HorizontalLimitLineYLabel:Maximum severity`,
          color: '#3d3d40',
          assignToAxisIds: {
            y: 100,
          },
        },
      ],
    },
    sf366: <ChartEvaluation>{
      evaluationCode: EvaluationService.EVALUATION_SF36,
      hidden: false,
      title: $localize`:@@patient_evaluationSF36_SF366Title:Social Functioning (SF)`,
      order: 6,
      features: [
        {
          featureId: FeatureService.FEATURE_SF36_6_SCR,
          label: $localize`:@@patient_evaluationSF36_featureSF36ScoreLabel:Score`,
          min: 0,
          max: 100,
          chartDatasetOption: {
            type: 'bar',
            color: '#8893aa',
            nbSteps: 10,
            tooltip: {
              label: $localize`:@@patient_evaluationSF36_featureSF36ScoreLabel:Score`,
              unit: '',
            },
          },
        },
      ],
      horizontalLimitLines: [
        {
          label: $localize`:@@patient_evaluationSF36_featureSF36HorizontalLimitLineYLabel:Maximum severity`,
          color: '#3d3d40',
          assignToAxisIds: {
            y: 100,
          },
        },
      ],
    },
    sf367: <ChartEvaluation>{
      evaluationCode: EvaluationService.EVALUATION_SF36,
      hidden: false,
      title: $localize`:@@patient_evaluationSF36_SF367Title:Bodily-Pain (BP)`,
      order: 3,
      features: [
        {
          featureId: FeatureService.FEATURE_SF36_7_SCR,
          label: $localize`:@@patient_evaluationSF36_featureSF36ScoreLabel:Score`,
          min: 0,
          max: 100,
          chartDatasetOption: {
            type: 'bar',
            color: '#8893aa',
            nbSteps: 10,
            tooltip: {
              label: $localize`:@@patient_evaluationSF36_featureSF36ScoreLabel:Score`,
              unit: '',
            },
          },
        },
      ],
      horizontalLimitLines: [
        {
          label: $localize`:@@patient_evaluationSF36_featureSF36HorizontalLimitLineYLabel:Maximum severity`,
          color: '#3d3d40',
          assignToAxisIds: {
            y: 100,
          },
        },
      ],
    },
    sf368: <ChartEvaluation>{
      evaluationCode: EvaluationService.EVALUATION_SF36,
      hidden: false,
      title: $localize`:@@patient_evaluationSF36_SF368Title:General Health (GH)`,
      order: 7,
      features: [
        {
          featureId: FeatureService.FEATURE_SF36_8_SCR,
          label: $localize`:@@patient_evaluationSF36_featureSF36ScoreLabel:Score`,
          min: 0,
          max: 100,
          chartDatasetOption: {
            type: 'bar',
            color: '#8893aa',
            nbSteps: 10,
            tooltip: {
              label: $localize`:@@patient_evaluationSF36_featureSF36ScoreLabel:Score`,
              unit: '',
            },
          },
        },
      ],
      horizontalLimitLines: [
        {
          label: $localize`:@@patient_evaluationSF36_featureSF36HorizontalLimitLineYLabel:Maximum severity`,
          color: '#3d3d40',
          assignToAxisIds: {
            y: 100,
          },
        },
      ],
    },
  };
  public chartDescription: ChartDescription;
  public dateRangeForm: FormGroup;
  public loading = true;
  public loadingChart = true;
  public datasets: any = {};
  public xLabels: any = {};
  public measuringPointsTotal = 0;
  public progressions = {};

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    private featureService: FeatureService,
    private chartService: ChartService,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.prepareDateRangeForm();
  }

  /**
   * Prepares date range form
   */
  prepareDateRangeForm(): void {
    const startDate = moment.utc().subtract(12, 'months').format('MM/YYYY');
    const endDate = moment.utc().format('MM/YYYY');

    this.dateRangeForm = this.formBuilder.group({
      startDate: [
        startDate,
        {
          validators: [
            Validators.required,
            Validators.pattern(/^(0[1-9]|1[012])\/[0-9]{4}$/),
          ],
          updateOn: 'blur',
        },
      ],
      endDate: [
        endDate,
        {
          validators: [
            Validators.required,
            Validators.pattern(/^(0[1-9]|1[012])\/[0-9]{4}$/),
          ],
          updateOn: 'blur',
        },
      ],
    });

    this._observers.add(
      this.dateRangeForm.valueChanges.pipe(pairwise()).subscribe({
        next: ([previousRes, res]) => this.submitDateRangeForm(),
      })
    );

    this.loading = false;
  }

  /**
   * Changes date range
   * @param event
   * @param key
   */
  changeDateRange(
    event: { monthIndex: number; year: number },
    key: string
  ): void {
    const value = moment
      .utc([event.year, event.monthIndex, 1])
      .format('MM/YYYY');

    this.dateRangeForm.get(key).setValue(value);
  }

  /**
   * Gets date year
   * @param key
   * @returns year
   */
  getDateYear(key: string): number {
    let splitDate = this.dateRangeForm.get(key).value.split('/');
    return splitDate[1];
  }

  /**
   * Gets date month
   * @param key
   * @returns month
   */
  getDateMonth(key: string): number {
    let splitDate = this.dateRangeForm.get(key).value.split('/');
    return parseInt(splitDate[0], 10) - 1;
  }

  /**
   * Submits date range
   */
  submitDateRangeForm(): void {
    if (this.dateRangeForm.valid) {
      this.loadingChart = true;
      this.setCharts();
    }
  }

  /**
   * Sets charts
   */
  setCharts(): void {
    let sources$ = {};

    for (const [key, evaluation] of Object.entries(this.evaluations)) {
      this.xLabels[key] = [] as ChartLabel[];
      this.datasets[key] = [] as ChartDataset[];
      this._features[key] = [] as Feature[];

      const startDate = moment.utc(
        this.dateRangeForm.get('startDate').value,
        'MM/YYYY'
      );
      const endDate = moment.utc(
        this.dateRangeForm.get('endDate').value,
        'MM/YYYY'
      );

      let params = {
        page: 1,
        startDate: startDate.toISOString(),
        endDate: endDate.endOf('month').toISOString(),
        patientUuid: this.route.snapshot.params['uuid'],
        featureIds: evaluation.features.map((item) => item.featureId),
      };

      sources$[key] = this.featureService.getFeatures(params).pipe(
        expand((res) => {
          params.page++;
          this._features[key] = this._features[key].concat(res);

          return res.length < 50
            ? EMPTY
            : this.featureService.getFeatures(params);
        })
      );
    }

    forkJoin(sources$)
      .pipe(finalize(() => (this.loadingChart = false)))
      .subscribe({
        next: (res) => {
          for (const [key, value] of Object.entries(this._features)) {
            let chartDataPoints = {};

            this.evaluations[key].features.forEach((feature) => {
              let groupedFeatures = this.featureService.groupFeatures(
                value as Feature[],
                feature.featureId
              );
              chartDataPoints[feature.featureId] =
                this.chartService.setChartDataPoints(groupedFeatures);
            });

            if (Object.values(chartDataPoints).length > 0) {
              // Set labels
              const maxDataPoints = Object.values(chartDataPoints).reduce(
                (r: any, s: any) => (r.length > s.length ? r : s),
                0
              );
              this.xLabels[key] = this.chartService.setXLabels(
                maxDataPoints as ChartDataPoint[]
              );

              // Set datasets
              this.evaluations[key].features.forEach((feature, j) => {
                const dataset = this.chartService.setDataset(
                  feature,
                  chartDataPoints[feature.featureId],
                  feature.chartDatasetOption,
                  [],
                  this.xLabels[key],
                  feature.min ??
                    (chartDataPoints[feature.featureId].length
                      ? chartDataPoints[feature.featureId][0].min
                      : null),
                  feature.max ??
                    (chartDataPoints[feature.featureId].length
                      ? chartDataPoints[feature.featureId][0].max
                      : null),
                  feature.isMaxLimited,
                  feature.isMinLimited
                );
                this.datasets[key].push(dataset);

                feature['progression'] = this.chartService.getProgression(
                  chartDataPoints[feature.featureId],
                  true
                );
              });

              if (this.datasets[key][0]) {
                // Set measuring points total
                this.measuringPointsTotal = this.datasets[key][0].data.filter(
                  (item) => item.value !== null
                ).length;

                // Set progression
                this.progressions[key] = this.chartService.getProgression(
                  this.datasets[key][0].data,
                  true
                );
              }
            }
          }
        },
      });
  }

  /**
   * Gets not hidden dimensions number
   * @returns number
   */
  getDisplayedDimensions(): number {
    return Object.values(this.evaluations).filter((item) => !item.hidden)
      .length;
  }

  /**
   * Toggles dimension display
   * @param hidden
   * @param index
   */
  toggleHideDimension(hidden: boolean, index: string): void {
    this.evaluations[index].hidden = !hidden;
  }
}
